import { type MouseEvent } from 'react';
import { getScopeRole } from '@/services/current-context';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { INTERNAL_CONSOLE_PREFIX, ROUTES } from '@/config/routes';
import { useNavigationService } from '@/services/navigate-service';
import { getPermissionForRoute } from '@/util/permissions';
import { isInternalAdmin, isInternalRole } from '@/util/roles';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { SideMenuAccordionConfig, SubMenu } from './side-menu.types';

// ---------------------------------------------------------------------------
// Route matching
// ---------------------------------------------------------------------------
// NavLink's default `end={false}` would activate BOTH `properties` and
// `properties/new` simultaneously when the path is `/home/properties/new`,
// because both are prefixes of the pathname at a segment boundary. We need
// "most-specific sibling wins" semantics so that the active rail indicator
// follows exactly one row. We also need the parent of dynamic detail routes
// (e.g. `/home/properties/:id`) to stay active for the bare `properties` row,
// which a naive `end` flag would break. Hence the custom matchers below feed
// NavLink's `className` callback rather than relying on its built-in matching.
// ---------------------------------------------------------------------------

// Internal console sub-routes live under INTERNAL_CONSOLE_PREFIX (DV-3520),
// everything else under /home/. Derived from ROUTES, so a console page added
// there (or a prefix move) needs no second edit here.
const INTERNAL_CONSOLE_ROOT = `${INTERNAL_CONSOLE_PREFIX}/`;
const INTERNAL_ROUTES = new Set(
  Object.values(ROUTES)
    .filter((path) => path.startsWith(INTERNAL_CONSOLE_ROOT))
    .map((path) => path.slice(INTERNAL_CONSOLE_ROOT.length))
);

export function routePrefixFor(route: string): string {
  return INTERNAL_ROUTES.has(route) ? `${INTERNAL_CONSOLE_ROOT}${route}` : `/home/${route}`;
}

export function isActiveRoute(pathname: string, route: string): boolean {
  const full = routePrefixFor(route);
  // Exact match OR the path continues with a '/' (prevents 'orders' matching 'orders/new')
  return pathname === full || pathname.startsWith(`${full}/`);
}

/** True only if this sub-item is the MOST SPECIFIC match among siblings */
export function isBestMatch(pathname: string, route: string, siblings: SubMenu[]): boolean {
  if (!isActiveRoute(pathname, route)) return false;
  // If a sibling has a longer matching route, this item is NOT the best match
  return !siblings.some(
    (s) => s.route && s.route !== route && s.route.startsWith(route) && isActiveRoute(pathname, s.route),
  );
}

/**
 * Returns true for modifier-clicks / middle-clicks that the browser's native
 * anchor handling should own (open-in-new-tab, open-in-new-window, etc.).
 * We let those pass through to NavLink's default behavior; for plain clicks
 * we preventDefault and route through `navigateTo` so the unsaved-changes
 * guard in `useNavigationService` still gets a chance to intervene.
 */
function isModifiedClick(e: MouseEvent<HTMLAnchorElement>): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

// ---------------------------------------------------------------------------
// Row sizing per variant
// ---------------------------------------------------------------------------

const VARIANT_ROWS = {
  sidebar: {
    section: 'px-2.5 py-2 text-[15px]',
    sub: 'px-2 py-1.5 text-sm',
    subIndent: 'pl-9'
  },
  drawer: {
    // 44px tap floor: min-h-11 on every interactive row (WCAG 2.5.8 AAA
    // floor asserted by the e2e suite at touch viewports).
    section: 'min-h-11 px-3 py-2 text-base',
    sub: 'min-h-11 px-3 py-2 text-[15px]',
    subIndent: 'pl-11'
  }
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface NavSectionsProps {
  sections: SideMenuAccordionConfig[];
  /** 'sidebar' = desktop rail (compact rows, tooltips when collapsed); 'drawer' = mobile sheet (≥44px rows, never collapsed). */
  variant: 'sidebar' | 'drawer';
  /** Icon-rail mode — sidebar variant only. */
  collapsed?: boolean;
  /** Called after a navigation has been dispatched (the drawer closes itself with this). */
  onNavigate?: () => void;
  /**
   * Accordion open-state, owned by a single `useNavAccordion()` call in the
   * shell that mounts this (and any sibling) `NavSections` instance — see
   * that hook's doc comment for why this can't be owned per-instance.
   */
  openSections: Set<string>;
  onToggleSection: (key: string) => void;
}

export function NavSections({
  sections,
  variant,
  collapsed = false,
  onNavigate,
  openSections,
  onToggleSection,
}: NavSectionsProps) {
  const location = useLocation();
  const currentView = new URLSearchParams(location.search).get('view');
  const { navigateTo } = useNavigationService();

  // The drawer is never collapsed, regardless of what the caller passes.
  const isCollapsed = variant === 'sidebar' && collapsed;
  const rows = VARIANT_ROWS[variant];

  const navigate = (path: string | undefined) => {
    if (!path) return;
    navigateTo(routePrefixFor(path));
    onNavigate?.();
  };

  // Plain-click handler for NavLinks: preventDefault and route through
  // `navigateTo` so the unsaved-changes guard runs. Modifier-clicks fall
  // through to NavLink's default anchor behavior (open in new tab, etc.).
  // `navigateTo(path, queryParams)` treats `path` as a pathname and serializes
  // `queryParams` into the search string — so any query (e.g. the Orders
  // preset `?view=`) must be passed via queryParams, never embedded in `path`
  // (React Router throws if a '?' appears in a manual `to.pathname`).
  const handleNavLinkClick =
    (path: string, queryParams: Record<string, string> = {}) =>
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (isModifiedClick(e)) return;
      e.preventDefault();
      navigateTo(path, queryParams);
      onNavigate?.();
    };

  // -- Render helpers -------------------------------------------------------

  const renderSubItem = (sub: SubMenu, siblings: SubMenu[]) => {
    if (!sub.route) return null;
    const base = routePrefixFor(sub.route);
    const to = sub.view ? `${base}?view=${sub.view}` : base;

    const groupUsesViews =
      sub.view !== undefined || siblings.some((s) => s.view !== undefined);
    const active = groupUsesViews
      ? isActiveRoute(location.pathname, sub.route) &&
        (currentView ?? 'all') === (sub.view ?? 'all')
      : isBestMatch(location.pathname, sub.route, siblings);

    return (
      <NavLink
        key={sub.title}
        to={to}
        tabIndex={sub.tabIndex}
        onClick={handleNavLinkClick(base, sub.view ? { view: sub.view } : {})}
        className={[
          `relative flex w-full cursor-pointer items-center ${rows.sub} transition-colors`,
          isCollapsed ? 'justify-center' : rows.subIndent,
          active
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        ].join(' ')}
      >
        {active && (
          <span className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
        )}
        {!isCollapsed && <span className="truncate">{sub.title}</span>}
      </NavLink>
    );
  };

  const renderSection = (config: SideMenuAccordionConfig) => {
    // Permission check (customer sidebar sections)
    if (config.routePermission && !getPermissionForRoute(config.routePermission)) {
      return null;
    }
    // Role gate (internal-console sections) — replaces the old
    // title-matched 'Admin Console' special case, and closes the hole where
    // admin pages rendered every section for any role that reached them.
    const role = getScopeRole();
    if (config.requiredRole === 'internal-admin' && !isInternalAdmin(role)) return null;
    if (config.requiredRole === 'internal' && !isInternalRole(role)) return null;

    const Icon = config.icon;
    const isOpen = openSections.has(config.eventKey ?? '');
    const hasChildren = config.subMenuList && config.subMenuList.length > 0;

    // No-children item (e.g., Orders in unauthenticated mode)
    if (!hasChildren) {
      if (!config.route) return null;
      const to = routePrefixFor(config.route);
      const active = config.parentRoute
        ? isActiveRoute(location.pathname, config.parentRoute)
        : false;

      // Collapsed-state active treatment uses only the icon color so the
      // light-red bg doesn't fill the entire rail row. Hover bg still works.
      const stateClasses = !active
        ? 'text-foreground hover:bg-accent'
        : isCollapsed
          ? 'text-primary hover:bg-accent'
          : 'bg-primary/10 text-primary font-medium';

      const link = (
        <NavLink
          key={config.route}
          to={to}
          tabIndex={config.tabIndex}
          onClick={handleNavLinkClick(to)}
          className={[
            `relative flex w-full cursor-pointer items-center gap-3 ${rows.section} transition-colors`,
            isCollapsed ? 'justify-center' : '',
            stateClasses,
          ].join(' ')}
        >
          <Icon className="h-[18px] w-[18px] shrink-0" />
          {!isCollapsed && <span className="truncate">{config.title}</span>}
        </NavLink>
      );

      if (isCollapsed) {
        return (
          <Tooltip key={config.eventKey}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{config.title}</TooltipContent>
          </Tooltip>
        );
      }
      return link;
    }

    // Section with children
    const defaultRoute = config.subMenuList?.[0]?.route;
    const sectionActive = !!config.subMenuList?.some(
      (sub) => sub.route && isActiveRoute(location.pathname, sub.route),
    );
    // Only color the section icon in collapsed state — when expanded the
    // active sub-item carries the highlight, so the parent stays neutral.
    const headerStateClasses =
      isCollapsed && sectionActive
        ? 'text-primary hover:bg-accent'
        : 'text-foreground hover:bg-accent';
    const sectionHeader = (
      <button
        onClick={() => (isCollapsed ? navigate(defaultRoute) : onToggleSection(config.eventKey ?? ''))}
        className={[
          `flex w-full cursor-pointer items-center gap-3 ${rows.section} transition-colors`,
          isCollapsed ? 'justify-center' : '',
          headerStateClasses,
        ].join(' ')}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" />
        {!isCollapsed && (
          <>
            <span className="flex-1 truncate text-left font-medium">{config.title}</span>
            <ChevronRight
              className={[
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                isOpen ? 'rotate-90' : '',
              ].join(' ')}
            />
          </>
        )}
      </button>
    );

    if (isCollapsed) {
      return (
        <Tooltip key={config.eventKey}>
          <TooltipTrigger asChild>{sectionHeader}</TooltipTrigger>
          <TooltipContent side="right">{config.title}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <div key={config.eventKey}>
        {sectionHeader}
        {isOpen && (
          <div className="mt-0.5 flex flex-col gap-0.5">
            {config.subMenuList!.map((sub) => renderSubItem(sub, config.subMenuList!))}
          </div>
        )}
      </div>
    );
  };

  return <>{sections.map(renderSection)}</>;
}
